import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, ChevronUp, MessageCircle, Maximize2, Search, SquarePen, X,
} from 'lucide-react';
import { apiFetch, useAuth } from '../../../context/AuthContext';
import { useMiniChat } from '../context/MiniChatContext';
import ConversationAvatar from './ConversationAvatar';
import ChatWindow from './ChatWindow';
import { getAvatarColor } from '../utils/formatters';
import { isSameUser, getFullInboxPath } from '../utils/userHelpers';
import {
  DOCK_EDGE, DOCK_HEIGHT, DOCK_RADIUS, DOCK_WIDTH, getDockSurfaces,
} from '../style/miniChatStyles';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

// Public/unauthenticated routes — the dock has nothing to show before login
// and shouldn't render on top of them.
const HIDDEN_ON_ROUTES = ['/', '/login', '/reset-password'];

const DAY_MS = 24 * 60 * 60 * 1000;

// "Mohamed Ahmed Kamal Zaki" -> "Mohamed Ahmed" — matches the truncation
// already used for notification sender names elsewhere in the app.
const firstTwoWords = (name) => {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).join(' ');
};

// Group rows: concatenate member names and truncate — "Mohamed A. and Moha…"
const groupRowLabel = (conv) => {
  if (!conv.isGroup) return firstTwoWords(conv.englishName || conv.name);
  const full = conv.name || conv.englishName || '';
  return full.length > 28 ? `${full.slice(0, 27).trimEnd()}…` : full;
};

// `conv.time` falls back to a full locale date ("8/26/2026") past a week,
// which is too wide for the row's time column — shorten those to "Aug 26"
// (or "26/8/25" for other years). Recent "2d"/"5m"-style labels pass through.
const rowTime = (conv, isRTL) => {
  if (!conv.time) return '';
  const ts = Number(conv.timestamp);
  if (!ts || Date.now() - ts < 7 * DAY_MS) return conv.time;
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return conv.time;
  const locale = isRTL ? 'ar-u-nu-latn' : 'en-US';
  return date.getFullYear() === new Date().getFullYear()
    ? date.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    : date.toLocaleDateString(locale, { day: 'numeric', month: 'numeric', year: '2-digit' });
};

const CountBadge = ({ count, color }) => {
  if (!count) return null;
  return (
    <span
      style={{
        minWidth: '18px', height: '18px', padding: '0 5px', boxSizing: 'border-box',
        borderRadius: '9px', background: color, color: '#fff',
        fontSize: '11px', fontWeight: 700, lineHeight: 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

const MiniChatDock = () => {
  const {
    conversations, setConversations, openWindows, openChat, closeChat, typingUsers, onlineUsers,
    employeeProfilePics, allEmployees, c, isRTL, t, theme, isMessengerPageOpen, miniChatDisabled,
  } = useMiniChat();

  const { hasFeatureAccess } = useAuth();

  // ── Admin-level feature gating (registry page "messages") ────────────────
  // Same idiom as MessengerPage: only constrains promoted users in Admin
  // View; user/manager views untouched. hasFeatureAccess bypasses for
  // admin/superadmin roles.
  const dockViewType =
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';
  const isAdminViewGated = dockViewType === 'admin';
  const canViewMessages = !isAdminViewGated || hasFeatureAccess('messages', 'view');
  const canSendMessages = !isAdminViewGated || hasFeatureAccess('messages', 'send');
  const canCreateGroup = !isAdminViewGated || hasFeatureAccess('messages', 'createGroup');

  // 'collapsed' | 'panel' | 'newMessage'
  const [dockState, setDockState] = useState('collapsed');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]); // multi-select for group start
  const [creatingGroup, setCreatingGroup] = useState(false);
  const searchRef = useRef(null);
  const containerRef = useRef(null);

  // The dock is a floating widget meant for desktop multitasking — on mobile
  // there's no room for it alongside the app's own bottom nav / topbar, and
  // it just sits on top of page content. Hide it entirely below the same
  // mobile breakpoint used everywhere else in the messenger.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  const hasActiveWindow = openWindows.some((w) => !w.minimized);
  // A chat window opening takes over — collapse the panel/compose back down.
  const panelVisible = (dockState === 'panel' || dockState === 'newMessage') && !hasActiveWindow;

  const resetToCollapsed = () => {
    setDockState('collapsed');
    setSearch('');
    setSelectedIds([]);
  };

  // Clicking anywhere outside the widget (panel, launcher, or an open chat
  // window) collapses everything back down to just the launcher pill.
  useEffect(() => {
    if (!panelVisible && openWindows.length === 0) return;
    const onPointerDown = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      // Media viewer / forward / delete-confirm / message-actions menus are
      // React-portaled to document.body, so they're never a DOM descendant
      // of the widget above — without this check, clicking inside any of
      // them (e.g. confirming a delete) would look like an outside click
      // and slam the whole widget shut mid-action.
      if (e.target.closest?.('[data-modal-root]')) return;
      resetToCollapsed();
      openWindows.forEach((w) => closeChat(w.chatId));
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelVisible, openWindows, closeChat]);

  // Shared with ChatWindow so the launcher, panel and window read as one widget.
  const surfaces = getDockSurfaces(c, theme);

  const isConvOnline = (id) => Array.from(onlineUsers || []).some((oid) => isSameUser(oid, id));

  const visibleConversations = useMemo(() => (
    [...conversations]
      .filter((conv) => !conv.isArchived)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  ), [conversations]);

  const totalUnread = visibleConversations.reduce((sum, conv) => sum + (conv.unread || 0), 0);
  const recentAvatars = visibleConversations.slice(0, 3);

  const query = search.trim().toLowerCase();
  const filteredConversations = query
    ? visibleConversations.filter((conv) =>
        `${conv.name || ''} ${conv.englishName || ''}`.toLowerCase().includes(query))
    : visibleConversations;

  // "New message" picker — the full org's visibility-scoped employee list,
  // same source the full Messenger page's compose flow uses.
  const filteredEmployees = useMemo(() => {
    const list = allEmployees || [];
    if (!query) return list;
    return list.filter((emp) =>
      `${emp.name || ''} ${emp.englishName || ''}`.toLowerCase().includes(query));
  }, [allEmployees, query]);

  const toggleSelected = (employee) => {
    setSelectedIds((prev) => {
      const id = String(employee.id);
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // Without the "create group" permission, dock composing stays 1:1 —
      // picking a new person replaces rather than adds to the selection.
      if (!canCreateGroup) return [id];
      return [...prev, id];
    });
  };

  const openExistingOrNewChat = (employee) => {
    const empId = String(employee.id);
    const existing = conversations.find((conv) => String(conv.id) === empId);
    if (existing) {
      openChat(existing);
      return;
    }
    const newConv = {
      id: empId,
      name: employee.name,
      englishName: employee.englishName,
      arabicName: employee.arabicName,
      avatar: employee.avatar,
      company: employee.company,
      department: employee.department,
      position: employee.position,
      email: employee.email,
      lastMessage: '',
      time: '',
      timestamp: Date.now(),
      unread: 0,
      online: isConvOnline(empId),
    };
    setConversations((prev) => [newConv, ...prev]);
    openChat(newConv);
  };

  // Single selection -> open/start that 1:1 chat directly. Two or more ->
  // create a named group (backend requires a name + 2+ members), naming it
  // the same way existing group rows are labelled (concatenated, truncated).
  const startChat = async () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length === 1) {
      const employee = filteredEmployees.find((e) => String(e.id) === selectedIds[0])
        || (allEmployees || []).find((e) => String(e.id) === selectedIds[0]);
      if (employee) openExistingOrNewChat(employee);
      resetToCollapsed();
      return;
    }

    const members = selectedIds
      .map((id) => (allEmployees || []).find((e) => String(e.id) === id))
      .filter(Boolean);
    if (members.length < 2) return;

    const autoName = members.map((m) => firstTwoWords(m.englishName || m.name)).join(', ');
    setCreatingGroup(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/chat-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: autoName, members: members.map((m) => m.id) }),
      });
      if (!response.ok) throw new Error('Failed to create group');
      const created = await response.json();
      const groupId = `group-${created.id}`;
      const group = {
        id: groupId,
        name: created.name,
        englishName: created.name,
        avatar: created.name.trim().split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase(),
        isGroup: true,
        lastMessage: '',
        time: t.justNow,
        timestamp: Date.now(),
        unread: 0,
        members: created.members || [],
      };
      setConversations((prev) => (prev.some((c2) => c2.id === groupId) ? prev : [group, ...prev]));
      openChat(group);
      resetToCollapsed();
    } catch (err) {
      console.error('Failed to create group chat:', err);
    } finally {
      setCreatingGroup(false);
    }
  };

  // "You: " prefix for 1:1 previews when the last message was ours — not
  // reliable for groups (backend doesn't return a last-sender id for those
  // yet), so groups just show the plain preview text.
  const rowPreview = (conv, typing) => {
    if (typing) return t.typing;
    if (!conv.lastMessage) return conv.online ? (isRTL ? 'نشط الآن' : 'Active now') : '';
    const prefix = !conv.isGroup && conv.isFromMe ? (isRTL ? 'أنت: ' : 'You: ') : '';
    return `${prefix}${conv.lastMessage}`;
  };

  const goToFullInbox = () => {
    navigate(getFullInboxPath());
    resetToCollapsed();
  };

  const openPanel = () => setDockState('panel');

  const openCompose = () => {
    setDockState('newMessage');
    setSearch('');
    setSelectedIds([]);
    requestAnimationFrame(() => searchRef.current?.focus());
  };

  const backToList = () => {
    setDockState('panel');
    setSearch('');
    setSelectedIds([]);
  };

  // Header X (panel or chat window): collapse all the way back to the
  // launcher pill — not one step back to the conversation list.
  const closeDock = () => {
    resetToCollapsed();
    openWindows.forEach((w) => closeChat(w.chatId));
  };

  // Admin-view users without the messages "view" feature get no dock at all.
  if (!canViewMessages) return null;
  // User turned it off from Settings.
  if (miniChatDisabled) return null;
  // No room for a floating dock on mobile — hide it there entirely.
  if (isMobile) return null;
  // The full inbox is its own, complete chat surface — don't show a second,
  // redundant one on top of it.
  if (isMessengerPageOpen) return null;
  if (HIDDEN_ON_ROUTES.includes(location.pathname)) return null;
  if (visibleConversations.length === 0) return null;

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;
  const isComposing = dockState === 'newMessage';
  const avatarColors = { ...c, sidebarBg: surfaces.popupBg };

  // Every hover/focus state lives here rather than in onMouseEnter handlers,
  // so rows and buttons carry NO inline background that would beat `:hover`.
  // All selectors are dock-prefixed — nothing leaks to the rest of the app.
  const dockCss = `
    .mini-chat-scroll { scrollbar-width: none; -ms-overflow-style: none; }
    .mini-chat-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }

    .mcd-pop { animation: mcdPop 0.18s cubic-bezier(0.16, 1, 0.3, 1) both; }
    @keyframes mcdPop {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to { opacity: 1; transform: none; }
    }

    .mcd-icon-btn {
      width: 32px; height: 32px; padding: 0; flex-shrink: 0;
      display: inline-flex; align-items: center; justify-content: center;
      border: none; border-radius: 8px; background: transparent;
      color: ${surfaces.muted}; cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .mcd-icon-btn.mcd-round { border-radius: 50%; }
    .mcd-icon-btn:hover { background: ${surfaces.hover}; color: ${surfaces.text}; }

    /* Launcher: an icon-only circle that widens on hover to reveal the
       "Messages" label and the recent-chat avatars. Width comes from the
       children's max-width (auto widths can't animate), so the pill grows
       out of whichever screen edge it's pinned to. */
    .mcd-launcher { transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1); }
    .mcd-launcher:hover, .mcd-launcher:focus-within { transform: translateY(-2px); }
    .mcd-launcher-icon { transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1); }
    .mcd-launcher:hover .mcd-launcher-icon { transform: scale(1.08); }
    .mcd-reveal {
      display: flex; align-items: center;
      max-width: 0; opacity: 0; overflow: hidden;
      transform: translateX(${isRTL ? '10px' : '-10px'});
      transition: max-width 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                  margin-inline-start 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                  transform 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                  opacity 0.5s ease;
    }
    .mcd-launcher:hover .mcd-reveal,
    .mcd-launcher:focus-within .mcd-reveal { max-width: 240px; opacity: 1; transform: none; }
    .mcd-launcher:hover .mcd-launcher-label,
    .mcd-launcher:focus-within .mcd-launcher-label { margin-inline-start: 10px; }
    /* The avatars trail the label in, so the pill unfolds in two beats
       rather than everything arriving at once. Delay is only on the hover
       rule — collapsing back stays immediate instead of feeling sluggish. */
    .mcd-launcher:hover .mcd-launcher-extra,
    .mcd-launcher:focus-within .mcd-launcher-extra { transition-delay: 0.2s; }

    @media (prefers-reduced-motion: reduce) {
      .mcd-launcher, .mcd-launcher-icon, .mcd-reveal, .mcd-pop { transition: none; animation: none; }
    }

    .mcd-pill-main { background: transparent; transition: background 0.15s ease; }
    .mcd-pill-main:hover { background: ${surfaces.hover}; }
    .mcd-pill-avatar { transition: transform 0.15s ease; }
    .mcd-pill-avatar:hover { transform: translateY(-2px); }

    .mcd-row { background: transparent; transition: background 0.12s ease; }
    .mcd-row:hover, .mcd-row[data-active="true"] { background: ${surfaces.hover}; }

    .mcd-search { transition: border-color 0.15s ease; }
    .mcd-search:focus-within { border-color: ${surfaces.accent} !important; }
    .mcd-search input::placeholder,
    .mini-chat-window textarea::placeholder { color: ${surfaces.muted}; opacity: 1; }

    .mcd-icon-btn:focus-visible, .mcd-row:focus-visible,
    .mcd-pill-main:focus-visible, .mcd-pill-avatar:focus-visible {
      outline: 2px solid ${surfaces.accent}; outline-offset: -2px;
    }

    /* Per-message "more" buttons show on hover only. MessengerPage has the
       same rule, but its stylesheet never loads while the dock is visible. */
    .mini-chat-window .msg-info-btn { opacity: 0; transition: opacity 0.15s ease, color 0.15s ease; }
    .mini-chat-window .message-wrapper:hover .msg-info-btn { opacity: 0.75; }
    .mini-chat-window .msg-info-btn:hover,
    .mini-chat-window .msg-info-btn:focus-visible { opacity: 1; color: ${surfaces.text} !important; }
  `;

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: '12px',
    width: '100%', textAlign: 'start', padding: '8px',
    border: 'none', borderRadius: '12px', cursor: 'pointer',
    color: surfaces.text, fontFamily: 'inherit',
  };

  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const emptyState = (
    <div style={{ padding: '40px 16px', textAlign: 'center', color: surfaces.muted, fontSize: '13px' }}>
      {t.tryDifferent}
    </div>
  );

  // ── State A: collapsed launcher ────────────────────────────────────────
  // An icon-only circle at rest. Hovering it (or tabbing into it) widens it
  // into a pill showing the "Messages" label, the recent-chat avatars and
  // the show-more chevron. The unread count rides on the icon itself so it
  // stays visible in both states.
  const collapsedPill = (
    <div
      className="mcd-pop mcd-launcher"
      style={{
        display: 'flex', alignItems: 'center',
        height: '48px', padding: '0 6px', boxSizing: 'border-box',
        background: surfaces.popupBg, border: `1px solid ${surfaces.border}`,
        borderRadius: '999px', boxShadow: surfaces.shadow,
      }}
    >
      <button
        type="button"
        className="mcd-pill-main"
        onClick={openPanel}
        title={isRTL ? 'الرسائل' : 'Messages'}
        aria-label={isRTL ? 'الرسائل' : 'Messages'}
        style={{
          display: 'flex', alignItems: 'center',
          height: '36px', padding: '0 8px', border: 'none', borderRadius: '999px',
          cursor: 'pointer', color: surfaces.text, fontFamily: 'inherit',
        }}
      >
        <span className="mcd-launcher-icon" style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
          <MessageCircle size={20} />
          {totalUnread > 0 && (
            <span style={{ position: 'absolute', top: '-6px', insetInlineEnd: '-8px' }}>
              <CountBadge count={totalUnread} color={surfaces.unread} />
            </span>
          )}
        </span>
        <span
          className="mcd-reveal mcd-launcher-label"
          style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          {t.messages}
        </span>
      </button>

      <div className="mcd-reveal mcd-launcher-extra">
        {recentAvatars.length > 0 && (
          <>
            <span aria-hidden="true" style={{ width: '1px', height: '20px', background: surfaces.border, flexShrink: 0, marginInlineStart: '4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
            {recentAvatars.map((conv, idx) => (
              <button
                key={conv.id}
                type="button"
                className="mcd-pill-avatar"
                onClick={() => openChat(conv)}
                title={conv.englishName || conv.name}
                aria-label={conv.englishName || conv.name}
                style={{
                  position: 'relative', display: 'flex', padding: 0,
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  borderRadius: '50%', boxShadow: `0 0 0 2px ${surfaces.popupBg}`,
                  marginInlineStart: idx === 0 ? 0 : '-8px',
                  zIndex: recentAvatars.length - idx,
                }}
              >
                <ConversationAvatar
                  conv={conv}
                  size={30}
                  employeeProfilePics={employeeProfilePics}
                  getAvatarColor={getAvatarColor}
                  isOnline={isConvOnline(conv.id)}
                  unreadCount={0}
                  c={avatarColors}
                  active={false}
                />
              </button>
            ))}
            </div>
          </>
        )}

        <button
          type="button"
          className="mcd-icon-btn mcd-round"
          onClick={openPanel}
          title={isRTL ? 'عرض المزيد' : 'Show more'}
          aria-label={isRTL ? 'عرض المزيد' : 'Show more'}
        >
          <ChevronUp size={18} />
        </button>
      </div>
    </div>
  );

  // ── State B / C: expanded panel ────────────────────────────────────────
  const startDisabled = selectedIds.length === 0 || creatingGroup;

  const panel = panelVisible && (
    <div
      className="mcd-pop"
      style={{
        width: DOCK_WIDTH,
        maxWidth: `calc(100vw - ${DOCK_EDGE * 2}px)`,
        height: DOCK_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: surfaces.popupBg,
        border: `1px solid ${surfaces.border}`,
        borderRadius: DOCK_RADIUS,
        boxShadow: surfaces.shadow,
        overflow: 'hidden',
      }}
    >
      {/* Header — same 56px bar and icon buttons as the chat window's */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          height: '56px', flexShrink: 0,
          paddingInlineStart: isComposing ? '8px' : '16px', paddingInlineEnd: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {isComposing && (
            <button
              type="button"
              className="mcd-icon-btn"
              onClick={backToList}
              title={isRTL ? 'رجوع' : 'Back'}
              aria-label={isRTL ? 'رجوع' : 'Back'}
            >
              <BackIcon size={18} />
            </button>
          )}
          <span style={{ fontSize: '16px', fontWeight: 700, color: surfaces.text, ...ellipsis }}>
            {isComposing ? t.newMessages : t.messages}
          </span>
          {!isComposing && <CountBadge count={totalUnread} color={surfaces.unread} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
          {/* {!isComposing && canSendMessages && (
            <button
              type="button"
              className="mcd-icon-btn"
              onClick={openCompose}
              title={t.newMessages}
              aria-label={t.newMessages}
            >
              <SquarePen size={17} />
            </button>
          )} */}
          {!isComposing && (
            <button
              type="button"
              className="mcd-icon-btn"
              onClick={goToFullInbox}
              title={isRTL ? 'فتح صفحة الرسائل' : 'Open full inbox'}
              aria-label={isRTL ? 'فتح صفحة الرسائل' : 'Open full inbox'}
            >
              <Maximize2 size={16} />
            </button>
          )}
          <button
            type="button"
            className="mcd-icon-btn"
            onClick={closeDock}
            title={isRTL ? 'إغلاق' : 'Close'}
            aria-label={isRTL ? 'إغلاق' : 'Close'}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Search — filters conversations, or "To:" people search when composing */}
      <div style={{ padding: '0 12px 8px', flexShrink: 0 }}>
        <div
          className="mcd-search"
          onClick={() => searchRef.current?.focus()}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            height: '36px', padding: '0 10px', boxSizing: 'border-box',
            background: surfaces.fieldBg, border: '1px solid transparent',
            borderRadius: '10px', cursor: 'text',
          }}
        >
          {isComposing ? (
            <span style={{ fontSize: '13px', fontWeight: 600, color: surfaces.muted, flexShrink: 0 }}>
              {isRTL ? 'إلى:' : 'To:'}
            </span>
          ) : (
            <Search size={15} color={surfaces.muted} style={{ flexShrink: 0 }} />
          )}
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchPeople}
            aria-label={t.searchPeople}
            style={{
              flex: 1, minWidth: 0, padding: 0, border: 'none', outline: 'none',
              background: 'transparent', color: surfaces.text,
              fontSize: '13.5px', fontFamily: 'inherit',
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t.miniChatClose}
              style={{
                border: 'none', background: 'transparent', color: surfaces.muted,
                cursor: 'pointer', display: 'flex', padding: 0,
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Rows — spacing only, no divider lines */}
      <div
        className="mini-chat-scroll"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 8px' }}
      >
        {isComposing ? (
          filteredEmployees.length === 0 ? emptyState : (
            filteredEmployees.map((emp) => {
              const label = firstTwoWords(emp.englishName || emp.name);
              const checked = selectedIds.includes(String(emp.id));
              return (
                <button
                  key={emp.id}
                  type="button"
                  className="mcd-row"
                  data-active={checked}
                  onClick={() => toggleSelected(emp)}
                  aria-label={emp.englishName || emp.name}
                  aria-pressed={checked}
                  style={rowStyle}
                >
                  <ConversationAvatar
                    conv={emp}
                    size={40}
                    employeeProfilePics={employeeProfilePics}
                    getAvatarColor={getAvatarColor}
                    isOnline={isConvOnline(emp.id)}
                    unreadCount={0}
                    c={avatarColors}
                    active={false}
                  />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: surfaces.text, ...ellipsis }}>
                      {label}
                    </span>
                    {(emp.department || emp.position) ? (
                      <span style={{ fontSize: '12.5px', color: surfaces.muted, ...ellipsis }}>
                        {emp.department || emp.position}
                      </span>
                    ) : null}
                  </div>
                  <span
                    style={{
                      width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                      border: `2px solid ${checked ? surfaces.accent : surfaces.border}`,
                      background: checked ? surfaces.accent : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {checked && <Check size={12} color="#fff" strokeWidth={3} />}
                  </span>
                </button>
              );
            })
          )
        ) : filteredConversations.length === 0 ? emptyState : (
          filteredConversations.map((conv) => {
            const isOpen = openWindows.some((w) => String(w.chatId) === String(conv.id));
            const typing = conv.isGroup
              ? false // group typing isn't implemented anywhere in the app yet
              : typingUsers.has(String(conv.id));
            const label = groupRowLabel(conv);
            const hasUnread = (conv.unread || 0) > 0;
            const preview = rowPreview(conv, typing);
            const time = rowTime(conv, isRTL);

            return (
              <button
                key={conv.id}
                type="button"
                className="mcd-row"
                data-active={isOpen}
                // openChat already handles the "already open" case (restores +
                // refreshes it in place) — always routing through it here means
                // clicking an already-open (even non-minimized) conversation
                // still brings it to front / focuses it, instead of doing
                // nothing when `isOpen` was true.
                onClick={() => openChat(conv)}
                aria-label={conv.englishName || conv.name}
                style={rowStyle}
              >
                {/* Avatar + green online dot only — the numeric badge lives
                    on the launcher, and unread is signalled by the blue dot */}
                <ConversationAvatar
                  conv={conv}
                  size={44}
                  employeeProfilePics={employeeProfilePics}
                  getAvatarColor={getAvatarColor}
                  isOnline={isConvOnline(conv.id)}
                  unreadCount={0}
                  c={avatarColors}
                  active={false}
                />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {/* Line 1: name + time, so every row's time lines up in one column */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        flex: 1, minWidth: 0, fontSize: '14px',
                        fontWeight: hasUnread ? 700 : 600, color: surfaces.text, ...ellipsis,
                      }}
                    >
                      {label}
                    </span>
                    {time ? (
                      <span
                        style={{
                          flexShrink: 0, fontSize: '11.5px', whiteSpace: 'nowrap',
                          fontWeight: hasUnread ? 600 : 400,
                          color: hasUnread ? surfaces.unread : surfaces.muted,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {time}
                      </span>
                    ) : null}
                  </div>
                  {/* Line 2: preview + unread dot */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '18px' }}>
                    <span
                      style={{
                        flex: 1, minWidth: 0, fontSize: '13px',
                        fontWeight: hasUnread ? 500 : 400,
                        color: typing ? surfaces.unread : (hasUnread ? surfaces.text : surfaces.muted),
                        fontStyle: typing ? 'italic' : 'normal',
                        ...ellipsis,
                      }}
                    >
                      {preview}
                    </span>
                    {hasUnread && (
                      <span
                        aria-hidden="true"
                        style={{
                          width: '9px', height: '9px', borderRadius: '50%',
                          background: surfaces.unread, flexShrink: 0,
                        }}
                      />
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Compose footer — full-width Chat button */}
      {isComposing && (
        <div style={{ padding: '10px 12px 12px', borderTop: `1px solid ${surfaces.divider}`, flexShrink: 0 }}>
          <button
            type="button"
            onClick={startChat}
            disabled={startDisabled}
            style={{
              width: '100%', height: '40px', border: 'none', borderRadius: '10px',
              background: startDisabled ? surfaces.fieldBg : surfaces.accent,
              color: startDisabled ? surfaces.muted : '#fff',
              fontWeight: 600, fontSize: '14px', fontFamily: 'inherit',
              cursor: startDisabled ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
          >
            {creatingGroup ? '…' : (isRTL ? 'محادثة' : 'Chat')}
          </button>
        </div>
      )}
    </div>
  );

  // A focused conversation view: once a chat window is open (from either the
  // avatar stack or a panel row), hide the launcher pill entirely so only the
  // chat is visible — it reappears on its own once the window closes/is
  // minimized, since dockState itself never changes on open.
  const launcher = dockState === 'collapsed' && !hasActiveWindow ? collapsedPill : null;

  return (
    // `display: contents` — a real DOM node (so the outside-click ref can
    // attach to it) that otherwise behaves exactly like the Fragment this
    // used to be: no box, no layout footprint, doesn't affect the app shell.
    <div ref={containerRef} style={{ display: 'contents' }}>
      <style>{dockCss}</style>

      <div
        style={{
          position: 'fixed',
          bottom: DOCK_EDGE,
          [isRTL ? 'left' : 'right']: DOCK_EDGE,
          zIndex: 950,
          display: 'flex',
          alignItems: 'flex-end',
          direction: isRTL ? 'rtl' : 'ltr',
        }}
      >
        {launcher}
        {panel}
      </div>

      {openWindows.map((w, index) => (
        <ChatWindow
          key={w.chatId}
          chatId={w.chatId}
          minimized={w.minimized}
          index={index}
          onCloseDock={closeDock}
        />
      ))}
    </div>
  );
};

export default MiniChatDock;
